import {
  ActionIcon,
  Button,
  CSSVariablesResolver,
  Card,
  DEFAULT_THEME,
  DefaultMantineColor,
  Divider,
  Loader,
  MantineColorsTuple,
  MantineTheme,
  MultiSelect,
  NumberInput,
  Popover,
  Select,
  Switch,
  Table,
  Tabs,
  ThemeIcon,
  alpha,
  colorsTuple,
  createTheme,
  defaultVariantColorsResolver,
  getPrimaryShade,
  isVirtualColor,
  mergeMantineTheme,
  parseThemeColor,
  rem,
  virtualColor,
} from "@mantine/core";
import type { VariantColorsResolver } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import React from "react";

import { MeqLoader } from "~/components/MeqLoader";
import SvgIconRaw from "~/components/SvgIconRaw";
import {
  error,
  gray,
  grayBlue,
  grayDarkMode,
  rose,
  success,
  warning,
} from "~/utils/Colors";
import { DateFormat } from "~/utils/Types";

import tabsClasses from "./Tabs.module.css";

type ExtendedCustomColors = "quaterary" | DefaultMantineColor;

declare module "@mantine/core" {
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, MantineColorsTuple>;
  }
}

// Shared MultiSelect surface styling. The theme default below layers
// `pill: { display: "none" }` on top because most MultiSelects in the app are
// filter-style controls that render their own selection summary; components
// that need visible pills (e.g. the shared Form's multi-select, which shows
// selected values like the legacy MUI Chips) pass these styles without the
// pill override. An instance `styles` prop replaces the defaultProps styles
// wholesale, so this object is exported for those call sites to build on.
/**
 * For a Select/MultiSelect rendered inside a Popover. Keeps its option list
 * inside the popover's own dropdown rather than a body portal: the popover
 * dismisses on mousedown anywhere outside its DOM, and a portaled option list
 * counts as outside — it then unmounts once its exit fade ends, so a press
 * held longer than that fade selects nothing.
 */
export const POPOVER_COMBOBOX_PROPS = { withinPortal: false } as const;

export const multiSelectSurfaceStyles = {
  input: {
    cursor: "pointer",
    borderColor: "var(--mantine-color-border-secondary)",
    backgroundColor: "var(--mantine-color-bg-primary)",
  },
  pillsList: { cursor: "pointer" },
  dropdown: {
    backgroundColor: "var(--mantine-color-bg-primary)",
    borderColor: "var(--mantine-color-border-secondary)",
  },
  option: {
    borderRadius: "0.25rem",
  },
} as const;

/**
 * `secondary`: a neutral bordered control. Border and text colour come from the
 * repo's own tokens rather than Mantine's `default` variant, so a button sits
 * flush with the inputs beside it (which pin the same border token) in both
 * colour schemes.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  if (input.variant === "secondary") {
    return {
      background: "var(--mantine-color-bg-primary)",
      hover: "var(--mantine-color-default-hover)",
      color: "var(--mantine-color-text-secondary)",
      border: "1px solid var(--mantine-color-border-secondary)",
    };
  }
  return defaultVariantColorsResolver(input);
};

const themeOverride = createTheme({
  variantColorResolver,
  colors: {
    grayBlue: [
      grayBlue[50],
      grayBlue[100],
      grayBlue[200],
      grayBlue[300],
      grayBlue[400],
      grayBlue[500],
      grayBlue[600],
      grayBlue[700],
      grayBlue[800],
      grayBlue[900],
    ],
    grayLightMode: [
      gray[50],
      gray[100],
      gray[200],
      gray[300],
      gray[400],
      gray[500],
      gray[600],
      gray[700],
      gray[800],
      gray[900],
    ],
    grayDarkMode: [
      grayDarkMode[50],
      grayDarkMode[100],
      grayDarkMode[200],
      grayDarkMode[300],
      grayDarkMode[400],
      grayDarkMode[500],
      grayDarkMode[600],
      grayDarkMode[700],
      grayDarkMode[800],
      grayDarkMode[900],
    ],
    quarteraryLight: colorsTuple(gray[500]),
    quarteraryDark: colorsTuple(grayDarkMode[400]),
    quarterary: virtualColor({
      name: "quarterary",
      dark: "quarteraryDark",
      light: "quarteraryLight",
    }),

    gmpPink: [
      rose[50],
      rose[100],
      "#ff8c99", // GMP brand colour
      rose[300],
      rose[400],
      rose[500],
      rose[600],
      rose[700],
      rose[800],
      rose[900],
    ],
  },
  fontFamily: "Inter, sans-serif",
  // Mantine 9 changed the default border-radius from `sm` (4px) to `md` (8px).
  // Pin `sm` to preserve the 8.x appearance.
  defaultRadius: "sm",
  headings: { fontWeight: "600" },
  primaryColor: "grayBlue",
  primaryShade: {
    light: 6,
    dark: 5,
  },
  other: {
    bgSecondarySubtleLight: gray[25],
    bgSecondarySubtleDark: grayDarkMode[900],
  },
  components: {
    // MUI IconButton is ghost-like; `subtle` keeps that look as MUI IconButtons
    // migrate to ActionIcon. Call sites that want the Mantine `filled` default
    // must now say so explicitly.
    ActionIcon: ActionIcon.extend({
      defaultProps: {
        variant: "subtle",
      },
    }),
    // MUI-parity for outline buttons. The old MuiButton `outlinedPrimary`
    // painted text + border with `primary.main` (grayBlue[600] light /
    // grayBlue[500] dark) in BOTH schemes. Mantine matches that in light
    // (`--mantine-color-<c>-outline` == the filled shade there) but deliberately
    // lightens the outline colour in dark mode (primaryShade − 4 → grayBlue[1],
    // a pale near-white), so migrated outline buttons lost the brand colour in
    // dark mode. Repaint enabled buttons' text + border with the filled shade
    // (== `--mantine-primary-color-filled`, the same token the active tab uses).
    // `vars` (not `styles`) so the disabled rule — which sets `color`/`border`
    // directly — still wins and disabled buttons stay muted. Light mode is a
    // no-op (filled == outline there). Any explicit `color` shade or non-theme
    // colour is left untouched.
    Button: Button.extend({
      vars: (theme, props) => {
        if (props.variant !== "outline") {
          return { root: {} };
        }
        const parsed = parseThemeColor({
          color: props.color ?? theme.primaryColor,
          theme,
        });
        if (!parsed.isThemeColor || parsed.shade !== undefined) {
          return { root: {} };
        }
        return {
          root: {
            "--button-color": `var(--mantine-color-${parsed.color}-filled)`,
            "--button-bd": `${rem(1)} solid var(--mantine-color-${parsed.color}-filled)`,
          },
        };
      },
    }),
    Card: Card.extend({
      defaultProps: {
        shadow: "xs",
        radius: "md",
        padding: "md",
        withBorder: true,
      },
      styles: {
        root: {
          backgroundColor: "var(--mantine-color-bg-primary)",
          borderColor: "var(--mantine-color-border-secondary)",
        },
      },
    }),
    Divider: Divider.extend({
      styles: {
        root: { borderColor: "var(--mantine-color-border-secondary)" },
      },
    }),
    Loader: Loader.extend({
      defaultProps: {
        loaders: {
          ...Loader.defaultLoaders,
          meq: MeqLoader,
        },
        type: "meq",
      },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: {
        firstDayOfWeek: 0, // Sunday
        highlightToday: true,
        rightSection: React.createElement(function DatePickerRightSection() {
          return React.createElement(SvgIconRaw, {
            name: "Calendar",
            size: 20,
            color: "var(--mantine-color-quarterary-0)",
          });
        }),
        rightSectionPointerEvents: "none",
        valueFormat: DateFormat.SHORT,
        weekendDays: [],
        withCellSpacing: false,
        // Same input surface as the themed Select/MultiSelect — without it
        // the Mantine default renders a grey fill in dark mode.
        styles: {
          input: {
            cursor: "pointer",
            backgroundColor: "var(--mantine-color-bg-primary)",
            borderColor: "var(--mantine-color-border-secondary)",
          },
        },
      },
    }),
    MultiSelect: MultiSelect.extend({
      defaultProps: {
        checkIconPosition: "right",
        rightSectionPointerEvents: "none",
        rightSection: React.createElement(SvgIconRaw, {
          name: "ChevronDown",
          size: 20,
        }),
        searchable: true,
        styles: {
          ...multiSelectSurfaceStyles,
          pill: { display: "none" },
        },
      },
    }),
    // Match the themed selects' input border + surface so NumberInputs (e.g.
    // the All Images Body Number Range fields next to the SortBy select) don't
    // fall back to Mantine's darker default input border.
    NumberInput: NumberInput.extend({
      styles: {
        input: {
          borderColor: "var(--mantine-color-border-secondary)",
          backgroundColor: "var(--mantine-color-bg-primary)",
        },
      },
    }),
    Popover: Popover.extend({
      defaultProps: {
        styles: {
          dropdown: {
            backgroundColor: "var(--mantine-color-bg-primary)",
          },
        },
      },
    }),
    Select: Select.extend({
      defaultProps: {
        rightSectionPointerEvents: "none",
        rightSection: React.createElement(SvgIconRaw, {
          name: "ChevronDown",
          size: 20,
        }),
        searchable: true,
        styles: {
          input: {
            cursor: "pointer",
            borderColor: "var(--mantine-color-border-secondary)",
            backgroundColor: "var(--mantine-color-bg-primary)",
          },
          dropdown: {
            backgroundColor: "var(--mantine-color-bg-primary)",
            borderColor: "var(--mantine-color-border-secondary)",
          },
          option: {
            borderRadius: "0.25rem",
          },
        },
        withCheckIcon: false,
      },
    }),
    Switch: Switch.extend({
      defaultProps: {
        withThumbIndicator: false,
      },
    }),
    // House striping colour for any `striped` table (gray-50 / #F9FAFB). Only
    // applies when a table opts into `striped`; the white base + `striped`
    // toggle stay per-table. MeqTable does its own per-row striping and is
    // unaffected.
    Table: Table.extend({
      defaultProps: {
        stripedColor: "var(--mantine-color-bg-secondary)",
      },
    }),
    // MUI-parity for the migrated Mantine default tabs, restoring the old
    // MuiTab look:
    //  - 8px padding + a 48px min-height (the height, not wide padding, is what
    //    gave the label/underline breathing room — keeps horizontal padding
    //    tight at 8px)
    //  - font-weight 500 labels
    //  - the base divider line back to gray-200 (#EAECF0) via --tab-border-color
    //    (Mantine's default gray-3 read too dark/heavy; width stays the
    //    variant's 1px)
    //  - active label recoloured to primary (grayBlue) — Tabs.module.css, since
    //    the [data-active] selector can't be expressed as an inline style
    // Padding/min-height/weight go through `styles` (inline, so they always win
    // over Mantine's own tab rules).
    Tabs: Tabs.extend({
      styles: {
        root: {
          // Mantine only exposes --tabs-color / --tabs-radius to `vars`, so the
          // divider colour is set as an inline CSS variable here (inline wins
          // over Mantine's colour-scheme-scoped default regardless of CSS load
          // order).
          "--tab-border-color": "var(--mantine-color-border-secondary)",
        } as React.CSSProperties,
        tab: {
          minHeight: rem(48),
          padding: rem(8),
          fontWeight: 500,
        },
      },
      classNames: {
        list: tabsClasses.list,
        tab: tabsClasses.tab,
      },
    }),
    ThemeIcon: ThemeIcon.extend({
      defaultProps: {
        variant: "transparent",
      },
      vars: (_theme, props) => {
        if (props.variant === "transparent") {
          return {
            root: {
              "--ti-bg": "transparent",
              "--ti-bd": "transparent",
              "--ti-color": props.color
                ? `var(--mantine-color-${props.color}-filled)`
                : "currentColor",
            },
          };
        }
        return { root: {} };
      },
    }),
  },
});

// Mantine 9 switched the `light` variant tokens (--mantine-color-<c>-light,
// -light-hover and -light-color) from Mantine 8's translucent tints to solid
// colours, which changed dark mode dramatically. Rebuild the Mantine 8 values
// for every theme colour using the same formulas as the deprecated
// `v8CssVariablesResolver` (translucent alpha of the primary-shade colour for
// backgrounds, shade-based text colour).
const getV8LightVariantVariables = (theme: MantineTheme) => {
  const lightShade = getPrimaryShade(theme, "light");
  const darkShade = getPrimaryShade(theme, "dark");
  const light: Record<string, string> = {};
  const dark: Record<string, string> = {};

  Object.keys(theme.colors).forEach((name) => {
    const color = theme.colors[name];
    const lightTuple = isVirtualColor(color)
      ? theme.colors[color.light]
      : color;
    const darkTuple = isVirtualColor(color) ? theme.colors[color.dark] : color;

    light[`--mantine-color-${name}-light`] = alpha(lightTuple[lightShade], 0.1);
    light[`--mantine-color-${name}-light-hover`] = alpha(
      lightTuple[lightShade],
      0.12,
    );
    light[`--mantine-color-${name}-light-color`] =
      `var(--mantine-color-${name}-${lightShade})`;

    dark[`--mantine-color-${name}-light`] = alpha(
      darkTuple[Math.max(0, darkShade - 2)],
      0.15,
    );
    dark[`--mantine-color-${name}-light-hover`] = alpha(
      darkTuple[Math.max(0, darkShade - 2)],
      0.2,
    );
    dark[`--mantine-color-${name}-light-color`] =
      `var(--mantine-color-${name}-${Math.max(darkShade - 5, 0)})`;
  });

  return { light, dark };
};

export const cssVariablesResolver: CSSVariablesResolver = (theme) => {
  // Restore Mantine 8's translucent `light` variant behaviour as the base,
  // then layer our own overrides on top (they win via spread order).
  const v8LightVariant = getV8LightVariantVariables(theme);
  return {
    variables: {
      "--text-color": "var(--mantine-color-text-primary)",
      // NOTE: `--mantine-color-text` is set per-scheme in the light/dark blocks
      // below, NOT here. Mantine's own resolver assigns it inside each
      // scheme-scoped block (light: theme.black, dark: --mantine-color-dark-0),
      // and those `:root[data-mantine-color-scheme=…]` selectors outrank a bare
      // `:root` override — so setting it here silently did nothing and dark-mode
      // body text fell back to Mantine's grey dark-0 (#C9C9C9) instead of
      // text-primary (#F5F5F6).
      "--mantine-shadow-xs": "0 1px 2px 0 rgba(16, 24, 40, 0.05)",
    },
    light: {
      ...v8LightVariant.light,
      "--mantine-color-body": theme.other.bgSecondarySubtleLight,
      "--mantine-color-bg-active": gray[50],
      "--mantine-color-bg-disabled": gray[100],
      "--mantine-color-bg-error-secondary": error[100],
      "--mantine-color-bg-primary": "#FFFFFF",
      "--mantine-color-bg-primary-solid": gray[900],
      "--mantine-color-bg-quarterary": gray[200],
      "--mantine-color-bg-secondary": gray[50],
      "--mantine-color-bg-tertiary": gray[100],
      "--mantine-color-border-brand-solid": grayBlue[600],
      "--mantine-color-border-error-solid": error[600],
      "--mantine-color-border-primary": gray[300],
      "--mantine-color-border-secondary": gray[200],
      "--mantine-color-fg-brand-primary": grayBlue[600],
      "--mantine-color-fg-error-primary": error[600],
      "--mantine-color-fg-primary": gray[900],
      "--mantine-color-fg-quarterary": gray[500],
      "--mantine-color-fg-success-primary": success[600],
      "--mantine-color-fg-success-secondary": success[500],
      "--mantine-color-fg-warning-dark": warning[900],
      "--mantine-color-fg-warning-secondary": warning[500],
      // App-wide default text colour. Set here (and in `dark`) rather than in
      // `variables` so it wins over Mantine's per-scheme default — see the note
      // in `variables`. Body text and anything inheriting `color` flows through
      // this token.
      "--mantine-color-text": "var(--mantine-color-text-primary)",
      "--mantine-color-text-disabled": gray[500],
      "--mantine-color-text-error-primary": error[600],
      "--mantine-color-text-primary": gray[900],
      "--mantine-color-text-primary-on-brand": "#FFFFFF",
      "--mantine-color-text-secondary": gray[700],
      "--mantine-color-text-tertiary": gray[600],
      "--mantine-color-utility-error-100": error[100],
      "--mantine-color-utility-error-200": error[200],
      "--mantine-color-utility-error-300": error[300],
      "--mantine-color-utility-gray-300": gray[300],
      "--mantine-color-utility-gray-600": gray[600],
      "--mantine-color-utility-gray-700": gray[700],
      "--mantine-color-utility-gray-800": gray[800],
      "--mantine-color-utility-gray-blue-100": grayBlue[100],
      "--mantine-color-utility-gray-blue-300": grayBlue[300],
    },
    dark: {
      ...v8LightVariant.dark,
      "--mantine-color-body": theme.other.bgSecondarySubtleDark,
      "--mantine-color-bg-active": grayDarkMode[800],
      "--mantine-color-bg-disabled": grayDarkMode[800],
      "--mantine-color-bg-error-secondary": error[600],
      "--mantine-color-bg-primary": grayDarkMode[950],
      "--mantine-color-bg-primary-solid": grayDarkMode[900],
      "--mantine-color-bg-quarterary": grayDarkMode[700],
      "--mantine-color-bg-secondary": grayDarkMode[900],
      "--mantine-color-bg-tertiary": grayDarkMode[800],
      "--mantine-color-border-brand-solid": grayBlue[500],
      "--mantine-color-border-error-solid": error[500],
      "--mantine-color-border-primary": grayDarkMode[700],
      "--mantine-color-border-secondary": grayDarkMode[800],
      "--mantine-color-fg-brand-primary": grayBlue[500],
      "--mantine-color-fg-primary": "#FFFFFF",
      "--mantine-color-fg-error-primary": error[500],
      "--mantine-color-fg-quarterary": grayDarkMode[400],
      "--mantine-color-fg-success-primary": success[500],
      "--mantine-color-fg-success-secondary": success[400],
      "--mantine-color-fg-warning-dark": warning[900],
      "--mantine-color-fg-warning-secondary": warning[400],
      "--mantine-color-gmpPink-light-color": theme.colors.gmpPink[5],
      // App-wide default text colour — see the light block / `variables` note.
      // Without this, dark body text fell back to Mantine's grey dark-0
      // (#C9C9C9) instead of text-primary (grayDarkMode-50, #F5F5F6).
      "--mantine-color-text": "var(--mantine-color-text-primary)",
      "--mantine-color-text-disabled": grayDarkMode[500],
      "--mantine-color-text-error-primary": error[400],
      "--mantine-color-text-primary": grayDarkMode[50],
      "--mantine-color-text-primary-on-brand": grayDarkMode[900],
      "--mantine-color-text-secondary": grayDarkMode[300],
      "--mantine-color-text-tertiary": grayDarkMode[400],
      "--mantine-color-utility-error-100": error[900],
      "--mantine-color-utility-error-200": error[800],
      "--mantine-color-utility-error-300": error[700],
      "--mantine-color-utility-gray-300": grayDarkMode[700],
      "--mantine-color-utility-gray-600": grayDarkMode[400],
      "--mantine-color-utility-gray-700": grayDarkMode[300],
      "--mantine-color-utility-gray-800": grayDarkMode[200],
      "--mantine-color-utility-gray-blue-100": grayBlue[900],
      "--mantine-color-utility-gray-blue-300": grayBlue[700],
    },
  };
};

export const theme = mergeMantineTheme(DEFAULT_THEME, themeOverride);
export const getThemeClientOverrides = (client: string) => {
  switch (client.toLowerCase()) {
    case "gmp":
      return createTheme({
        primaryColor: "gmpPink",
        primaryShade: {
          light: 2,
          dark: 5,
        },
      });
    default:
      return {};
  }
};
